// src/zustand/eventTrackingStore.js

import { create } from "zustand";
import { eventTracker } from "../services/eventTracking";

/**
 * Zustand store for event tracking state management
 * This store includes the following state:
 * - isInitialized: boolean - Whether the event tracking is initialized
 * - sessionId: string - The session ID
 * - memberId: number - The member ID
 *
 * This store includes the following methods:
 * - initialize: async (sessionId: string, memberId: number) => Promise<void> - Initialize the event tracking, used in the EventTrackingInitializer component
 * - updateSession: (sessionId: string, memberId: number) => void - Update the session, used in the EventTrackingInitializer component
 * - clear: () => void - Clear the event tracking, used in the EventTrackingInitializer component and in the auth store
 * - getState: () => { isInitialized: boolean, sessionId: string, memberId: number } - Get the current state, used in the EventTrackingInitializer component and in the auth store
 *
 * This store is used to manage the event tracking state and provide a way to initialize, update, and clear the event tracking. It is used in the EventTrackingInitializer component and in the auth store.
 */
export const useEventTrackingStore = create((set, get) => ({
	// State
	isInitialized: false,
	sessionId: null,
	memberId: null,

	// Initialize event tracking
	initialize: async (sessionId, memberId) => {
		try {
			// Generate session ID if not provided
			const finalSessionId =
				sessionId ||
				`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

			// Initialize the event tracker
			await eventTracker.initialize(finalSessionId, memberId);

			set({
				isInitialized: true,
				sessionId: finalSessionId,
				memberId: memberId,
			});

			// console.log("Event tracking initialized:", {
			// 	sessionId: finalSessionId,
			// 	memberId,
			// });
		} catch (error) {
			console.error("Failed to initialize event tracking:", error);
		}
	},

	// Update session (e.g., after login)
	updateSession: (sessionId, memberId) => {
		const finalSessionId = sessionId || get().sessionId;

		eventTracker.updateSession(finalSessionId, memberId);

		set({
			sessionId: finalSessionId,
			memberId: memberId,
		});

		console.log("Event tracking session updated:", {
			sessionId: finalSessionId,
			memberId,
		});
	},

	// Clear tracking (e.g., on logout)
	clear: () => {
		eventTracker.clear();

		set({
			isInitialized: false,
			sessionId: null,
			memberId: null,
		});

		console.log("Event tracking cleared");
	},

	// Get current state
	getState: () => ({
		isInitialized: get().isInitialized,
		sessionId: get().sessionId,
		memberId: get().memberId,
	}),
}));
